// Stregeffekten fra designguiden: tynde lime-streger vokser mod hinanden og mødes
// i et lille glimt. Tegnes på et fast canvas uden pointer-events. Respekterer
// prefers-reduced-motion og pauser helt, når fanen er skjult.

type Line = { dx: number; dy: number; len: number };
type Spark = { mx: number; my: number; lines: Line[]; t0: number; grow: number; glint: number; fade: number };

const MAX_SPARKS = 4;

function hexToRgb(value: string): [number, number, number] {
    const m = value.trim().match(/^#([0-9a-f]{6})$/i);
    if (!m) return [184, 192, 32];
    const n = parseInt(m[1], 16);
    return [n >> 16, (n >> 8) & 255, n & 255];
}

function start(): void {
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.className = 'lines';
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const [r, g, b] = hexToRgb(getComputedStyle(document.documentElement).getPropertyValue('--acc'));
    const rgba = (a: number): string => `rgba(${r},${g},${b},${a})`;
    const ease = (t: number): number => 1 - Math.pow(1 - t, 3);
    const rnd = (min: number, max: number): number => min + Math.random() * (max - min);

    let w = 0, h = 0, dpr = 1;
    const resize = (): void => {
        dpr = Math.min(window.devicePixelRatio || 1, 2);
        w = document.documentElement.clientWidth;
        h = document.documentElement.clientHeight;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
    };
    window.addEventListener('resize', resize);
    resize();

    const sparks: Spark[] = [];
    const spawn = (t: number): void => {
        const len = (): number => rnd(140, Math.min(520, Math.max(w, h) * 0.45));
        const kind = Math.random();
        let lines: Line[];
        if (kind < 0.6) {
            const sx = Math.random() < 0.5 ? -1 : 1, sy = Math.random() < 0.5 ? -1 : 1;
            lines = [{ dx: sx, dy: 0, len: len() }, { dx: 0, dy: sy, len: len() }];
        } else if (kind < 0.85) {
            lines = [{ dx: -1, dy: 0, len: len() }, { dx: 1, dy: 0, len: len() }];
        } else {
            lines = [{ dx: 0, dy: -1, len: len() }, { dx: 0, dy: 1, len: len() }];
        }
        sparks.push({
            mx: window.scrollX + rnd(w * 0.08, w * 0.92),
            my: window.scrollY + rnd(h * 0.12, h * 0.9),
            lines, t0: t, grow: rnd(2600, 4200), glint: 700, fade: 1400
        });
    };

    const draw = (s: Spark, age: number): void => {
        const p = ease(Math.min(age / s.grow, 1));
        const fadeOut = age > s.grow + s.glint ? 1 - (age - s.grow - s.glint) / s.fade : 1;
        const a = Math.min(age / 900, 1) * fadeOut;

        for (const l of s.lines) {
            const sx = s.mx + l.dx * l.len, sy = s.my + l.dy * l.len;
            const hx = sx - l.dx * l.len * p, hy = sy - l.dy * l.len * p;
            const tx = sx - l.dx * l.len * p * 0.05, ty = sy - l.dy * l.len * p * 0.05;
            const grad = ctx.createLinearGradient(sx, sy, hx, hy);
            grad.addColorStop(0, rgba(0));
            grad.addColorStop(1, rgba(0.85 * a));
            ctx.strokeStyle = grad;
            ctx.lineWidth = 1.25;
            ctx.lineCap = 'round';
            ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(hx, hy); ctx.stroke();
            ctx.fillStyle = rgba(a);
            ctx.beginPath(); ctx.arc(hx, hy, 1.6, 0, Math.PI * 2); ctx.fill();
        }

        if (age <= s.grow) return;
        const gp = Math.min((age - s.grow) / s.glint, 1);
        const ga = (gp < 0.25 ? gp / 0.25 : 1 - (gp - 0.25) / 0.75) * fadeOut;
        const radius = 6 + gp * 22;
        const halo = ctx.createRadialGradient(s.mx, s.my, 0, s.mx, s.my, radius);
        halo.addColorStop(0, rgba(0.7 * ga));
        halo.addColorStop(1, rgba(0));
        ctx.fillStyle = halo;
        ctx.beginPath(); ctx.arc(s.mx, s.my, radius, 0, Math.PI * 2); ctx.fill();
        const arm = 4 + 10 * Math.sin(gp * Math.PI);
        ctx.strokeStyle = `rgba(255,255,240,${ga})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(s.mx - arm, s.my); ctx.lineTo(s.mx + arm, s.my);
        ctx.moveTo(s.mx, s.my - arm); ctx.lineTo(s.mx, s.my + arm);
        ctx.stroke();
        ctx.fillStyle = rgba(ga);
        ctx.beginPath(); ctx.arc(s.mx, s.my, 2.2, 0, Math.PI * 2); ctx.fill();
    };

    let frameId = 0, last = 0, next = 400;
    const frame = (t: number): void => {
        if (t - last > next && sparks.length < MAX_SPARKS) {
            spawn(t);
            last = t;
            next = rnd(1400, 2600);
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        ctx.translate(-window.scrollX, -window.scrollY);
        for (let i = sparks.length - 1; i >= 0; i--) {
            const s = sparks[i], age = t - s.t0;
            if (age > s.grow + s.glint + s.fade) sparks.splice(i, 1);
            else draw(s, age);
        }
        frameId = requestAnimationFrame(frame);
    };

    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            cancelAnimationFrame(frameId);
        } else {
            sparks.length = 0;
            last = performance.now();
            frameId = requestAnimationFrame(frame);
        }
    });

    frameId = requestAnimationFrame(frame);
}

if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) start();

export {};
